/**
 * kc-image-revival.js
 *
 * Ensures images load progressively as the visitor scrolls, and recovers
 * from failed/stuck loads automatically. Addresses three pain points:
 *
 *   1) PROGRESSIVE LOADING — Chrome's native `loading="lazy"` threshold
 *      is roughly the viewport height, which can feel "popping in" on
 *      slow connections, and sometimes never triggers if the image is
 *      inside a parent that becomes visible via JS (e.g. carousel tab
 *      hydration) without a scroll event near it.
 *      We observe every lazy image with a generous rootMargin so the
 *      browser starts fetching ~1500px before the image enters the
 *      viewport.
 *
 *   2) ERROR RETRY — when an image fires `error` (network blip, 5xx
 *      from the CDN, transient timeout), the browser leaves it in a
 *      broken state forever. We retry up to 3 times with backoff,
 *      adding a cache-busting query param to bypass any failed-response
 *      cache.
 *
 *   3) DYNAMIC INSERTIONS — MutationObserver picks up images added
 *      after initial parse (carousel tab hydration, AJAX swaps, Shopify
 *      editor re-renders) and applies the same protections to them.
 *
 *   4) HOVER VIDEOS — product-card hover videos (video[data-hover-video])
 *      play on mouseenter / pause on mouseleave so they download zero bytes
 *      until an actual hover (and never on touch / mobile).
 *
 * No external dependencies. Runs as a deferred ES module so it doesn't
 * block first paint.
 */

(function () {
  const LAZY_PRELOAD_MARGIN = '1500px';
  const RETRY_ATTEMPTS = 3;
  const RETRY_DELAY = 1500; // base ms, multiplied by attempt number

  /**
   * Promotes a lazy image to eager so the browser starts fetching it.
   * No-op if the image is already eager or already loaded.
   */
  function promoteToEager(img) {
    if (img.loading === 'eager' || img.complete) return;
    img.loading = 'eager';
  }

  /**
   * Retry handler — re-assign the src with a cache-buster after a delay.
   * Subsequent attempts use exponential backoff (1.5s, 3s, 4.5s).
   */
  function handleImageError(event) {
    const img = event.target;
    if (!img || img.tagName !== 'IMG' || !img.src) return;

    // Ignore images that don't have a real src (placeholders, data URIs, etc.)
    if (img.src.startsWith('data:')) return;

    const attempts = parseInt(img.dataset.kcRetryAttempts || '0', 10);
    if (attempts >= RETRY_ATTEMPTS) return;

    img.dataset.kcRetryAttempts = String(attempts + 1);
    const delay = RETRY_DELAY * (attempts + 1);

    setTimeout(function () {
      // Re-check: maybe a different code path already loaded it
      if (img.complete && img.naturalWidth > 0) return;
      try {
        const url = new URL(img.src, location.href);
        url.searchParams.set('_r', String(Date.now()));
        img.src = url.toString();
      } catch (_) {
        // src wasn't a valid URL — bail silently
      }
    }, delay);
  }

  let progressiveObserver = null;

  function getProgressiveObserver() {
    if (progressiveObserver) return progressiveObserver;
    if (!('IntersectionObserver' in window)) return null;

    progressiveObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          promoteToEager(entry.target);
          progressiveObserver.unobserve(entry.target);
        });
      },
      { rootMargin: LAZY_PRELOAD_MARGIN }
    );
    return progressiveObserver;
  }

  /**
   * Observe a single image for progressive loading.
   * Idempotent — calling twice on the same image is harmless.
   */
  function observeImage(img) {
    if (!img || img.tagName !== 'IMG') return;
    if (img.dataset.kcRevivalObserved) return;
    img.dataset.kcRevivalObserved = '1';

    if (img.loading === 'lazy' && !img.complete) {
      const io = getProgressiveObserver();
      if (io) io.observe(img);
    }
  }

  /**
   * Product-card hover videos (video[data-hover-video]) are rendered WITHOUT
   * autoplay and with preload="none", so they cost zero bytes until the
   * visitor actually hovers the card. Start playback on mouseenter, pause on
   * mouseleave. Using mouseenter/leave (not pointer events) keeps this to real
   * mouse hovers — touch devices never fetch the video. Idempotent.
   */
  function bindHoverVideo(video) {
    if (!video || video.dataset.kcHoverBound) return;
    video.dataset.kcHoverBound = '1';

    const card = video.closest('.kc-product-card') || video.parentElement;
    if (!card) return;

    card.addEventListener('mouseenter', function () {
      if (video.paused) video.play().catch(function () {});
    });
    card.addEventListener('mouseleave', function () {
      if (!video.paused) video.pause();
    });
  }

  /**
   * Decorative autoplay videos — <video-media autoplay><video preload="none">,
   * emitted by snippets/kc-video.liquid (kc-video / kc-media blocks).
   *
   * theme.js starts them once, when they first enter the viewport, and never
   * stops them: scrolling down a page with several clips left every one of them
   * downloading and decoding at the same time (3–22 MB of video per visit in the
   * Oct 2026 audit). Without touching theme.js:
   *
   * A clip that scrolls out of view is paused, and resumed when it comes back.
   * Only clips WE paused are resumed, so the first play — and its autoplay-blocked
   * fallback — stays theme.js' job. A clip that is not muted is left alone: the
   * visitor turned its sound on, so it is theirs to stop.
   */
  const autoplayVideos = new WeakSet();
  const pausedOffscreen = new WeakSet();
  let autoplayObserver = null;

  function getAutoplayObserver() {
    if (autoplayObserver || !('IntersectionObserver' in window)) return autoplayObserver;
    autoplayObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        const video = entry.target;
        if (entry.isIntersecting) {
          if (pausedOffscreen.has(video)) {
            pausedOffscreen.delete(video);
            if (video.paused) video.play().catch(function () {});
          }
        } else if (!video.paused && video.muted) {
          pausedOffscreen.add(video);
          video.pause();
        }
      });
    });
    return autoplayObserver;
  }

  function observeAutoplayVideo(video) {
    if (!video || video.tagName !== 'VIDEO' || autoplayVideos.has(video)) return;
    if (!video.closest('video-media[autoplay]')) return;
    autoplayVideos.add(video);

    const io = getAutoplayObserver();
    if (io) io.observe(video);
  }

  function init() {
    // 1) Observe all existing lazy images on the page.
    document.querySelectorAll('img[loading="lazy"]').forEach(observeImage);

    // 1b) Wire up hover-to-play for product-card hover videos.
    document.querySelectorAll('video[data-hover-video]').forEach(bindHoverVideo);

    // 1c) Decorative autoplay videos: pause while off screen.
    document.querySelectorAll('video-media[autoplay] video').forEach(observeAutoplayVideo);

    // 2) Watch for images added later (carousel tab hydration, AJAX,
    //    editor re-renders). MutationObserver fires for any DOM mutation
    //    under <body>; we filter to images.
    if ('MutationObserver' in window) {
      const mo = new MutationObserver(function (mutations) {
        mutations.forEach(function (m) {
          m.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return; // ELEMENT_NODE only
            if (node.tagName === 'IMG') {
              observeImage(node);
            } else if (node.tagName === 'VIDEO') {
              if (node.hasAttribute('data-hover-video')) bindHoverVideo(node);
              observeAutoplayVideo(node);
            } else if (node.querySelectorAll) {
              node.querySelectorAll('img[loading="lazy"]').forEach(observeImage);
              node.querySelectorAll('video[data-hover-video]').forEach(bindHoverVideo);
              node.querySelectorAll('video').forEach(observeAutoplayVideo);
            }
          });
        });
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }

    // 3) Global error retry — capture phase so we catch errors from
    //    images at any depth, including those added dynamically.
    document.addEventListener('error', handleImageError, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
